import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get tokens from URL hash
  const getTokenFromHash = (tokenName: string) => {
    const hash = window.location.hash.substring(1); // Remove the # symbol
    const params = new URLSearchParams(hash);
    return params.get(tokenName);
  };

  const access_token = searchParams.get("access_token") || getTokenFromHash("access_token");
  const refresh_token = searchParams.get("refresh_token") || getTokenFromHash("refresh_token");
  const { resetPassword, loading } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!access_token) {
      setError("Invalid reset token. Please request a new password reset link.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    try {
      await resetPassword(access_token, refresh_token, password);
      setSuccess(true);
    } catch (err: any) {
      setError(err.details || "Failed to reset password. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 h-screen flex items-center justify-center">
        <div className="w-full max-w-6xl grid grid-cols-2 gap-8 bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Left side - Info */}
          <div className="bg-gradient-to-br from-primary/90 to-primary p-12 text-white">
            <div className="h-full flex flex-col">
              <div>
                <h2 className="text-3xl font-bold mb-4">
                  Reset your password
                </h2>
                <p className="text-lg opacity-90 mb-12">
                  Choose a new secure password for your account
                </p>
              </div>

              <div className="mt-auto pt-12">
                <p className="text-sm text-white/80">
                  By resetting your password, you agree to our{" "}
                  <a href="/terms" className="text-white hover:underline">Terms of Service</a>
                  {" "}and{" "}
                  <a href="/privacy" className="text-white hover:underline">Privacy Policy</a>
                </p>
              </div>
            </div>
          </div>

          {/* Right side - Form */}
          <div className="p-12">
            <div className="max-w-md mx-auto">
              <button 
                onClick={() => navigate("/login")}
                className="inline-flex items-center text-sm text-gray-600 hover:text-primary mb-6"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to sign in
              </button>

              {success ? (
                <div className="space-y-6">
                  <div className="bg-green-50 text-green-800 p-4 rounded-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium">
                          Password reset successful!
                        </p>
                        <p className="mt-1 text-sm">
                          Your password has been updated. You can now sign in with your new password.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={() => navigate("/login")}
                    className="w-full"
                  >
                    Sign In
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full"
                      placeholder="Enter your new password"
                      minLength={8}
                    />
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password
                    </label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="w-full"
                      placeholder="Confirm your new password"
                      minLength={8}
                    />
                  </div>

                  {error && (
                    <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className={cn(
                      "w-full h-11 text-sm font-medium",
                      loading && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
